import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";

export interface ScanResult {
  /** The raw characters captured during the scan burst. */
  raw: string;
  /** The raw value split on the "|" / "~" delimiter. */
  parts: string[];
}

/** Matches a bare GUID (8-4-4-4-12 hex). */
export const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The default routing for a scan:
 *   "v|{vehicleId}|{vrm}"    -> the vehicle
 *   "p|{partId}|{tagNumber}" -> the part's stock page
 *   "bin|{binId}|{binName}"  -> the part bins admin page
 *   a bare GUID (no prefix)  -> the part's stock page (temporary)
 * Returns true if it navigated. Exposed so a page override can fall back to the
 * normal behaviour for scans it doesn't want to handle itself.
 */
export function navigateForScan(navigate: NavigateFunction, { parts }: ScanResult): boolean {
  if (parts.length >= 2 && parts[1]) {
    const prefix = parts[0].toLowerCase();
    if (prefix === "v") {
      navigate(`/vehicles/${parts[1]}`);
      return true;
    }
    if (prefix === "p") {
      navigate(`/stock/${parts[1]}`);
      return true;
    }
    // Bins have no page of their own, so a bin scanned outside a flow that wants it (allocating
    // parts on a job) lands on the bin list rather than doing nothing at all.
    if (prefix === "bin") {
      navigate(`/admin/part-bins`);
      return true;
    }
  }

  // Temporary: a bare GUID with no prefix or pipes is treated as a part id.
  if (parts.length === 1 && GUID_REGEX.test(parts[0])) {
    navigate(`/stock/${parts[0]}`);
    return true;
  }

  return false;
}

type ScanHandler = (scan: ScanResult) => void;

interface ScannerContextInterface {
  /**
   * Register a handler that takes over scanner input while the calling
   * component is mounted, suppressing the default navigation behaviour.
   * Returns an unregister function. Prefer the `useScannerOverride` hook.
   */
  registerOverride: (handler: ScanHandler) => () => void;
}

interface ScannerProviderInterface {
  children: React.ReactNode;
}

const ScannerContext = createContext<ScannerContextInterface | undefined>(undefined);

export function ScannerProvider({ children }: ScannerProviderInterface) {
  const navigate = useNavigate();

  // Stack of active overrides; the most recently mounted one wins so nested
  // pages/dialogs behave predictably.
  const overridesRef = useRef<ScanHandler[]>([]);
  const scanBufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const registerOverride = useCallback((handler: ScanHandler) => {
    overridesRef.current.push(handler);
    return () => {
      overridesRef.current = overridesRef.current.filter((h) => h !== handler);
    };
  }, []);

  useEffect(() => {
    // Max gap (ms) between keystrokes for them to be considered part of one scan.
    const SCAN_KEY_GAP_MS = 50;
    // Inactivity after the burst before we treat the buffer as a complete scan.
    // The scanner sends no Enter/terminator, so we flush on this quiet period.
    const SCAN_FLUSH_MS = 80;

    const flush = () => {
      const buffer = scanBufferRef.current;
      scanBufferRef.current = "";

      if (!buffer) return;

      // A scanner configured for a US keyboard layout emits "~" instead of
      // "|" on a UK layout host, so accept both as the delimiter.
      const parts = buffer.split(/[|~]/);
      console.log("[scan] flush — buffer:", JSON.stringify(buffer), "parts:", parts);

      // A page can take over scanner input; the most recent override wins.
      const override = overridesRef.current[overridesRef.current.length - 1];
      if (override) {
        override({ raw: buffer, parts });
        return;
      }

      navigateForScan(navigate, { raw: buffer, parts });
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const now = e.timeStamp;
      const elapsed = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      // Too slow to be part of the current burst — start a fresh scan.
      if (elapsed > SCAN_KEY_GAP_MS) {
        scanBufferRef.current = "";
      }

      // Some scanners do send Enter — flush immediately if so.
      if (e.key === "Enter") {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flush();
        return;
      }

      // Only accumulate single printable characters. Some events (native search
      // autofill/suggestions) fire keydown with an undefined key — ignore those.
      if (e.key?.length === 1) {
        scanBufferRef.current += e.key;
      }

      // Reset the inactivity timer; when keys stop arriving, flush the buffer.
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(flush, SCAN_FLUSH_MS);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, [navigate]);

  const value = useMemo(() => ({ registerOverride }), [registerOverride]);

  return <ScannerContext.Provider value={value}>{children}</ScannerContext.Provider>;
}

function useScannerContext() {
  const ctx = useContext(ScannerContext);
  if (!ctx) {
    throw new Error("useScannerContext must be used within a ScannerProvider");
  }
  return ctx;
}

/**
 * Take over barcode-scanner input while this component is mounted, suppressing
 * the default vehicle navigation. Pass a handler to process scans yourself, or
 * omit it (or pass a no-op) to simply disable scanning on this page.
 *
 * @example
 * // Disable scanner navigation on this page:
 * useScannerOverride();
 *
 * @example
 * // Handle scans yourself:
 * useScannerOverride(({ parts }) => addPartByTag(parts[0]));
 */
export function useScannerOverride(handler?: ScanHandler) {
  const { registerOverride } = useScannerContext();

  // Keep the latest handler in a ref so we register only once (on mount) and
  // don't re-subscribe every render when an inline handler identity changes.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return registerOverride((scan) => handlerRef.current?.(scan));
  }, [registerOverride]);
}
